/**
 * Caption Styles for Momento Video Exports
 *
 * Defines preset caption styles for Shotstack video rendering.
 * These styles support advanced features like rounded corners, custom fonts,
 * stroke effects, and precise positioning.
 */

export interface CaptionStyleFont {
  family: string;
  size: number;
  color: string;
  stroke?: string;
  strokeWidth?: number;
  lineHeight?: number;
}

export interface CaptionStyleBackground {
  color: string;
  opacity: number;
  padding: number;
  borderRadius: number;
}

export interface CaptionStylePosition {
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'center' | 'bottom';
  offsetX?: number;
  offsetY?: number;
}

export interface CaptionStyle {
  name: string;
  description: string;
  font: CaptionStyleFont;
  background: CaptionStyleBackground;
  position?: CaptionStylePosition;
}

export type CaptionStyleName = 'modern' | 'classic' | 'bold' | 'minimal' | 'none';

/**
 * Preset caption styles
 */
export const CAPTION_STYLES: Record<CaptionStyleName, CaptionStyle> = {
  modern: {
    name: 'Modern',
    description: 'Clean, rounded corners, semi-transparent - Perfect for social media',
    font: {
      // Sizes target ~3% of 1080p frame height (industry-standard subtitle size).
      // Previous 21px was ~2% — borderline at 1080p.
      family: 'Open Sans Bold',
      size: 32,
      color: '#FFFFFF',
      stroke: '#000000',
      strokeWidth: 2,
      lineHeight: 1.2
    },
    background: {
      color: '#000000',
      opacity: 0.8,
      padding: 16,
      borderRadius: 10
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom',
      offsetY: -50
    }
  },

  classic: {
    name: 'Classic',
    description: 'Professional, solid background - Best for corporate content',
    font: {
      family: 'Open Sans Bold',
      size: 36,
      color: '#FFFFFF',
      lineHeight: 1.1
    },
    background: {
      color: '#000000',
      opacity: 1,
      padding: 16,
      borderRadius: 0  // Sharp corners
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom',
      offsetY: -40
    }
  },

  bold: {
    name: 'Bold',
    description: 'High contrast yellow on red - Great for ads and promotional content',
    font: {
      // Earlier 56px @ 1080p (~5.2% of frame height) plus 20px padding and a
      // line-height of 1.3 made the caption box fill ~12% of the frame and
      // 30%+ of the width — the user reported it as "super big". Drop to
      // 40px (~3.7%, the upper end of broadcast-subtitle norms) and trim
      // padding so the emphasis comes from the colour, not the size.
      family: 'Permanent Marker',
      size: 40,
      color: '#FFFF00',
      stroke: '#000000',
      strokeWidth: 2,
      lineHeight: 1.2
    },
    background: {
      color: '#FF0000',
      opacity: 0.9,
      padding: 12,
      borderRadius: 8
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom',
      offsetY: -50
    }
  },

  minimal: {
    name: 'Minimal',
    description: 'Subtle, no background - Best for cinematic or artistic content',
    font: {
      family: 'Clear Sans Regular',
      // Sizes are rendered by Shotstack as pixels against the output
      // resolution. At 1080p the old 20px (~1.85% of frame height) was
      // below the readable floor and the 0.5px stroke was subpixel, so
      // the text was effectively invisible on any bright frame. These
      // values put the text at ~2.6% of a 1080p frame with a real
      // 2px stroke — still the quietest of the four presets, but actually
      // legible.
      size: 28,
      color: '#FFFFFF',
      stroke: '#000000',
      strokeWidth: 2,
      lineHeight: 1.2
    },
    background: {
      color: '#000000',
      opacity: 0,  // Transparent background — stroke does the legibility work
      padding: 0,
      borderRadius: 0
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom',
      offsetY: -30
    }
  },

  none: {
    name: 'No Captions',
    description: 'Video only, no captions - Best for when captions are not needed',
    font: {
      family: '',
      size: 0,
      color: '#000000',
      lineHeight: 1
    },
    background: {
      color: '#000000',
      opacity: 0,
      padding: 0,
      borderRadius: 0
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom'
    }
  }
};

/**
 * Get caption style configuration
 * Supports custom overrides
 */
export function getCaptionStyleConfig(
  styleName: CaptionStyleName | string,
  customConfig?: Partial<CaptionStyle>
): CaptionStyle {
  const baseStyle = CAPTION_STYLES[styleName as CaptionStyleName] || CAPTION_STYLES.modern;

  if (!customConfig) {
    return baseStyle;
  }

  // Deep merge custom config with base style
  return {
    ...baseStyle,
    name: customConfig.name || baseStyle.name,
    description: customConfig.description || baseStyle.description,
    font: {
      ...baseStyle.font,
      ...(customConfig.font || {})
    },
    background: {
      ...baseStyle.background,
      ...(customConfig.background || {})
    },
    position: {
      ...baseStyle.position,
      ...(customConfig.position || {})
    }
  };
}

/**
 * Get all available caption style names
 */
export function getAvailableCaptionStyleNames(): CaptionStyleName[] {
  return Object.keys(CAPTION_STYLES) as CaptionStyleName[];
}

/**
 * Get all available caption styles
 */
export function getAvailableCaptionStyles(): Record<CaptionStyleName, CaptionStyle> {
  return { ...CAPTION_STYLES };
}

/**
 * Convert caption style to CSS properties (for preview)
 * Approximates Shotstack rendering using CSS
 */
export function captionStyleToCSS(style: CaptionStyle): React.CSSProperties {
  return {
    fontFamily: style.font.family,
    fontSize: `${style.font.size}px`,
    color: style.font.color,
    lineHeight: style.font.lineHeight || 1.2,

    // Stroke effect using text-shadow
    textShadow: style.font.stroke && style.font.strokeWidth
      ? `0 0 ${style.font.strokeWidth * 2}px ${style.font.stroke}`
      : undefined,

    // Background
    backgroundColor: style.background.color,
    opacity: style.background.opacity,
    padding: `${style.background.padding}px`,
    borderRadius: `${style.background.borderRadius}px`,

    // Display
    display: 'inline-block',
    textAlign: 'center',
    maxWidth: '80%',

    // Position helpers
    ...(style.position?.horizontal === 'center' && {
      marginLeft: 'auto',
      marginRight: 'auto'
    })
  };
}

/**
 * Caption style metadata for UI selection
 */
export interface CaptionStyleOption {
  value: CaptionStyleName;
  label: string;
  description: string;
  preview: {
    fontSize: string;
    borderRadius: string;
    backgroundColor: string;
  };
}

/**
 * Get caption style options for UI selectors
 */
export function getCaptionStyleOptions(): CaptionStyleOption[] {
  return getAvailableCaptionStyleNames().map(styleName => {
    const style = CAPTION_STYLES[styleName];
    return {
      value: styleName,
      label: style.name,
      description: style.description,
      preview: {
        fontSize: `${style.font.size}px`,
        borderRadius: `${style.background.borderRadius}px`,
        backgroundColor: style.background.color
      }
    };
  });
}

/**
 * Validate custom caption config
 */
export function validateCaptionConfig(config: Partial<CaptionStyle>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (config.font) {
    if (config.font.size && (config.font.size < 12 || config.font.size > 72)) {
      errors.push('Font size must be between 12 and 72');
    }
    if (config.font.strokeWidth && (config.font.strokeWidth < 0 || config.font.strokeWidth > 5)) {
      errors.push('Stroke width must be between 0 and 5');
    }
  }

  if (config.background) {
    if (config.background.opacity !== undefined && (config.background.opacity < 0 || config.background.opacity > 1)) {
      errors.push('Background opacity must be between 0 and 1');
    }
    if (config.background.padding && (config.background.padding < 0 || config.background.padding > 100)) {
      errors.push('Background padding must be between 0 and 100');
    }
    if (config.background.borderRadius && (config.background.borderRadius < 0 || config.background.borderRadius > 50)) {
      errors.push('Border radius must be between 0 and 50');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Curated web-safe font list. Each entry's `src` is a publicly-hosted
 * TTF/OTF/WOFF/WOFF2 file Shotstack can fetch — when the user picks one
 * of these in the customizer we declare it via `timeline.fonts[]` so
 * Shotstack actually renders it. The `family` MUST match the font's
 * embedded family name; Shotstack resolves the `font.family` string in
 * the caption asset against the registered fonts.
 *
 * Sources are jsDelivr-hosted Google Fonts mirrors (stable CDN URLs
 * vs. the Google Fonts dynamic CSS that returns differently-named
 * variants per request).
 */
export interface WebSafeFont {
  family: string;
  src: string;
  description: string;
}

export const WEB_SAFE_FONTS: WebSafeFont[] = [
  {
    family: 'Inter',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/inter@latest/latin-700-normal.ttf',
    description: 'Modern, neutral — designed for screens'
  },
  {
    family: 'Montserrat',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/montserrat@latest/latin-700-normal.ttf',
    description: 'Geometric, friendly'
  },
  {
    family: 'Roboto',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-700-normal.ttf',
    description: 'Default-feeling, broad legibility'
  },
  {
    family: 'Poppins',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/poppins@latest/latin-600-normal.ttf',
    description: 'Bold, social-media-friendly'
  },
  {
    family: 'Playfair Display',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/playfair-display@latest/latin-700-normal.ttf',
    description: 'Serif headline — editorial feel'
  },
  {
    family: 'Bebas Neue',
    src: 'https://cdn.jsdelivr.net/fontsource/fonts/bebas-neue@latest/latin-400-normal.ttf',
    description: 'Tall, all-caps look'
  }
];

/**
 * Look up a curated font by family name. Returns the entry with its
 * Shotstack `src` URL when found — caller passes that to
 * `timeline.fonts[]` so the family resolves at render time.
 */
export function findWebSafeFont(family: string | undefined): WebSafeFont | undefined {
  if (!family) return undefined;
  return WEB_SAFE_FONTS.find(f => f.family === family);
}

/**
 * Create custom caption style from domain preferences
 */
export function createCustomStyleFromPreferences(preferences: {
  caption_font_family?: string;
  caption_font_size?: number;
  caption_font_color?: string;
  caption_stroke_color?: string;
  caption_stroke_width?: number;
  caption_bg_color?: string;
  caption_bg_opacity?: number;
  caption_border_radius?: number;
}): CaptionStyle {
  return {
    name: 'Custom',
    description: 'Your workspace custom style',
    font: {
      family: preferences.caption_font_family || 'Montserrat SemiBold',
      size: preferences.caption_font_size || 32,
      color: preferences.caption_font_color || '#FFFFFF',
      stroke: preferences.caption_stroke_color || '#000000',
      strokeWidth: preferences.caption_stroke_width || 1
    },
    background: {
      color: preferences.caption_bg_color || '#000000',
      opacity: preferences.caption_bg_opacity || 0.8,
      padding: 24,
      borderRadius: preferences.caption_border_radius || 12
    },
    position: {
      horizontal: 'center',
      vertical: 'bottom'
    }
  };
}
